import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Gift, HandCoins } from 'lucide-react';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';

import { ContributionDetailDialog } from '@/components/giving/contribution-detail-dialog';
import { RecordOfferingDialog } from '@/components/giving/record-offering-dialog';
import { VoidReasonDialog } from '@/components/giving/void-reason-dialog';
import {
  choosableOfferingDates,
  defaultOfferingDate,
  offeringCalendarDisabledDates,
  offeringCalendarMarkedDates,
} from '@/components/programs/gathering-schedule';
import type { ProgramDayDetail } from '@/components/programs/program-day-detail';
import { ChartCard } from '@/components/shared/chart-card';
import { CollapsibleInsights } from '@/components/shared/collapsible-insights';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import {
  DateRangeFilter,
  LIST_FILTER_DATE_RANGE_PRESETS,
  type DateRange,
} from '@/components/shared/date-range-filter';
import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { fundTypeLabel } from '@/components/shared/fund-type-select';
import { MemberNameLink } from '@/components/shared/member-name-link';
import { MoneyDisplay } from '@/components/shared/money-display';
import { PageHeader } from '@/components/shared/page-header';
import { GivingTrendChart } from '@/components/giving/giving-trend-chart';
import { SimpleBarChart } from '@/components/shared/simple-bar-chart';
import { StatCard } from '@/components/shared/stat-card';
import { Tip } from '@/components/shared/tip';
import { VenueSelect } from '@/components/shared/venue-select';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { MoneyInput } from '@/components/ui/money-input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import {
  dataTableMoneyColumnTotals,
  formatInsightsMoneySummary,
  formatOccurrenceTotalsSummary,
  insightsHasMultipleCurrencies,
  occurrenceMoneyTotals,
  occurrenceTotalsAmounts,
  type ListMoneyTotals,
  type OccurrenceTotals,
} from '@/lib/money-totals';
import { MoneyTotalsStack } from '@/components/shared/money-totals-stack';
import { ApiError } from '@/lib/api-errors';
import { hasPermission, PERMISSIONS } from '@/lib/permissions';
import {
  loadOfferingsPagePreferences,
  persistOfferingsPagePreferences,
  resolveStoredOfferingDateRange,
  toOfferingsPagePreferences,
} from '@/lib/offerings-page-preferences';
import { useChurchStore } from '@/stores/church-store';
import { usePreferences } from '@/stores/preferences-store';
import { formatDate, formatMoney, formatNumber, formatPercent } from '@/utils/format';

import { GivingSubnav } from './giving-subnav';

type GatheringKind = 'service' | 'program';

interface Program {
  id: string;
  name: string;
  kind: GatheringKind;
  scheduleType: string;
  scheduleRule: string | null;
  startsOn: string | null;
  endsOn: string | null;
  startTime: string | null;
  endTime: string | null;
  venueId: string | null;
  venueName: string | null;
}

interface Occurrence {
  id: string;
  programId: string;
  programName: string | null;
  programKind: GatheringKind | null;
  occurrenceDate: string;
  startsAt: string;
  endsAt: string;
  venueId: string | null;
  venueName: string | null;
  status: string;
  preacherName?: string | null;
  guestSpeaker?: string | null;
}

interface ServiceSummaryRow {
  occurrenceId: string;
  programName: string;
  occurrenceDate: string;
  currency: string;
  amountMinor: number;
  count: number;
}

interface Fund {
  id: string;
  name: string;
  fundType: string;
  defaultCurrency: string;
  isActive: boolean;
}

interface Contribution {
  id: string;
  fundId: string;
  memberId: string | null;
  giverName: string | null;
  identityHidden: boolean;
  amountMinor: number;
  currency: string;
  contributionDate: string;
  method: string;
  receiptNumber: number;
  isVoided: boolean;
  voidReason: string | null;
  voidedByName: string | null;
  updatedAt: string;
  canEdit: boolean;
  canVoid: boolean;
}

interface OccurrenceInsight {
  occurrence: Occurrence;
  funds: Array<{
    fundId: string;
    fundName: string;
    fundType: string;
    customTypeName?: string | null;
    currency: string;
    amountMinor: number;
    count: number;
  }>;
  totals: OccurrenceTotals;
}

interface Batch {
  id: string;
  status: string;
  expectedTotalMinor: number;
  actualTotalMinor: number;
  lineCount: number;
  currency: string;
}

interface OfferingsInsights {
  amountMinor: number;
  count: number;
  currency: string;
  byCurrency?: Array<{ currency: string; amountMinor: number; count: number }>;
  comparison: {
    change: number;
    previousAmountMinor: number;
    previousCount: number;
  };
  byFund: Array<{ fundId: string; fundName: string; amountMinor: number; currency: string }>;
  trend: Array<{
    key: string;
    label: string;
    amountMinor: number;
    count: number;
    grain?: 'day' | 'week' | 'month';
  }>;
}

const todayIso = () => new Date().toISOString().slice(0, 10);

const localeNs = (kind: GatheringKind) =>
  kind === 'service' ? 'services' : 'programOfferings';

export const ServiceOfferingsPage = () => {
  const { occurrenceId } = useParams();
  if (occurrenceId) {
    return <ServiceOfferingDetail occurrenceId={occurrenceId} />;
  }
  return <ServiceOfferingsList />;
};

const ServiceOfferingsList = () => {
  const { t } = useTranslation('giving');
  const [searchParams, setSearchParams] = useSearchParams();
  const church = useChurchStore((state) => state.church);
  const isModuleEnabled = useChurchStore((state) => state.isModuleEnabled);
  const timezone = usePreferences((state) => state.timezone) ?? church?.defaultTimezone ?? 'UTC';
  const programsEnabled = isModuleEnabled('programs');
  const tab: GatheringKind =
    searchParams.get('tab') === 'programs' ? 'program' : 'service';

  const [dateRange, setDateRange] = useState<DateRange>(() =>
    resolveStoredOfferingDateRange(loadOfferingsPagePreferences(church?.id), timezone),
  );

  useEffect(() => {
    setDateRange(
      resolveStoredOfferingDateRange(loadOfferingsPagePreferences(church?.id), timezone),
    );
  }, [church?.id, timezone]);

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    persistOfferingsPagePreferences(church?.id, toOfferingsPagePreferences(range));
  };

  if (!programsEnabled) {
    return (
      <div>
        <PageHeader
          title={t('offerings.title')}
          description={t('offerings.description')}
          tipId="giving.baseCurrency"
        />
        <GivingSubnav />
        <p className="text-body-sm text-fg-secondary">{t('offerings.programsRequired')}</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={t('offerings.title')}
        description={t('offerings.description')}
        tipId="giving.baseCurrency"
      />
      <GivingSubnav />
      <Tabs
        value={tab}
        onValueChange={(value) => {
          setSearchParams(value === 'program' ? { tab: 'programs' } : {}, { replace: true });
        }}
      >
        <TabsList>
          <TabsTrigger value="service">{t('offerings.tabs.services')}</TabsTrigger>
          <TabsTrigger value="program">{t('offerings.tabs.programs')}</TabsTrigger>
        </TabsList>
        <TabsContent value="service">
          <OfferingsKindPanel
            kind="service"
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
          />
        </TabsContent>
        <TabsContent value="program">
          <OfferingsKindPanel
            kind="program"
            dateRange={dateRange}
            onDateRangeChange={handleDateRangeChange}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const OfferingsKindPanel = ({
  kind,
  dateRange,
  onDateRangeChange,
}: {
  kind: GatheringKind;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
}) => {
  const { t } = useTranslation('giving');
  const { t: tPrograms } = useTranslation('programs');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);
  const timezone = usePreferences((state) => state.timezone) ?? church?.defaultTimezone ?? 'UTC';
  const ns = localeNs(kind);
  const ministryPath = kind === 'service' ? '/services' : '/programs';
  const canCreateGathering = hasPermission(membership, PERMISSIONS.PROGRAMS_CREATE);
  const canRecord = hasPermission(membership, PERMISSIONS.GIVING_RECORD);
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordSession, setRecordSession] = useState(0);

  const dateFrom = dateRange.from;
  const dateTo = dateRange.to;
  const [filterProgramId, setFilterProgramId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [programId, setProgramId] = useState<string | null>(null);
  const [serviceDate, setServiceDate] = useState<string | null>(todayIso());
  const [venueId, setVenueId] = useState<string | null>(null);

  const programs = useQuery({
    queryKey: ['programs', kind],
    queryFn: () => api.get<{ data: Program[] }>(`/programs?kind=${kind}&pageSize=100`),
  });

  const programRows = programs.data?.data ?? [];
  const hasPrograms = programRows.length > 0;
  const selectedProgram = useMemo(
    () => programRows.find((row) => row.id === programId) ?? null,
    [programId, programRows],
  );

  const today = DateTime.now().setZone(timezone).toFormat('yyyy-MM-dd');
  const offeringPastHorizon =
    DateTime.fromISO(today, { zone: timezone }).minus({ days: 366 }).toFormat('yyyy-MM-dd') ??
    today;
  const offeringFutureHorizon =
    DateTime.fromISO(today, { zone: timezone }).plus({ days: 366 }).toFormat('yyyy-MM-dd') ??
    today;

  const programDayDetails = useQuery({
    queryKey: ['programs', programId, 'day-details'],
    queryFn: () =>
      api.get<{ data: ProgramDayDetail[] }>(`/programs/${programId}/day-details`),
    enabled: createOpen && Boolean(programId),
  });

  const occurrences = useQuery({
    queryKey: ['occurrences', kind, dateFrom, dateTo, filterProgramId],
    queryFn: () => {
      const params = new URLSearchParams({
        pageSize: '50',
        status: 'scheduled',
        kind,
      });
      if (dateFrom) params.set('fromDate', dateFrom);
      if (dateTo) params.set('toDate', dateTo);
      if (filterProgramId) params.set('programId', filterProgramId);
      return api.get<{ data: Occurrence[] }>(`/occurrences?${params}`);
    },
  });

  const listRows = occurrences.data?.data ?? [];

  const programOccurrencesLookup = useQuery({
    queryKey: ['occurrences', programId, 'offering-lookup', kind],
    queryFn: () => {
      const params = new URLSearchParams({
        programId: programId!,
        pageSize: '100',
        status: 'scheduled',
        kind,
      });
      return api.get<{ data: Occurrence[] }>(`/occurrences?${params}`);
    },
    enabled: createOpen && Boolean(programId),
  });

  const occurrencesByDate = useMemo(() => {
    const map = new Map<string, Occurrence>();
    for (const row of programOccurrencesLookup.data?.data ?? []) {
      map.set(row.occurrenceDate, row);
    }
    return map;
  }, [programOccurrencesLookup.data?.data]);

  const datesOnList = useMemo(() => {
    if (!programId) return [];
    return listRows
      .filter((row) => row.programId === programId)
      .map((row) => row.occurrenceDate);
  }, [listRows, programId]);

  const selectableOfferingDates = useMemo(
    () =>
      selectedProgram ? choosableOfferingDates(selectedProgram, today, datesOnList) : [],
    [selectedProgram, today, datesOnList],
  );

  const offeringMarkedDates = useMemo(
    () =>
      selectedProgram ? offeringCalendarMarkedDates(selectedProgram, today) : [],
    [selectedProgram, today],
  );

  const offeringDisabledDates = useMemo(
    () =>
      offeringCalendarDisabledDates(
        selectableOfferingDates,
        offeringPastHorizon,
        offeringFutureHorizon,
      ),
    [selectableOfferingDates, offeringPastHorizon, offeringFutureHorizon],
  );

  const createDatesLoading = createOpen && Boolean(programId) && occurrences.isLoading;
  const noScheduleDates =
    !createDatesLoading &&
    Boolean(selectedProgram) &&
    offeringMarkedDates.length === 0;
  const allDaysOnList =
    !createDatesLoading &&
    Boolean(selectedProgram) &&
    offeringMarkedDates.length > 0 &&
    selectableOfferingDates.length === 0;

  const givingByService = useQuery({
    queryKey: ['giving', 'summary', 'by-service', kind, dateFrom, dateTo],
    queryFn: () => {
      const params = new URLSearchParams({ pageSize: '100', kind });
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      return api.get<{ data: ServiceSummaryRow[] }>(`/giving/summary/by-service?${params}`);
    },
  });

  const insights = useQuery({
    queryKey: ['giving', 'insights', 'offerings', kind, dateFrom, dateTo],
    queryFn: () =>
      api.get<OfferingsInsights>('/giving/insights', {
        query: {
          programKind: kind,
          dateFrom: dateFrom ?? undefined,
          dateTo: dateTo ?? undefined,
        },
      }),
  });

  const totalsByOccurrence = useMemo(() => {
    const map = new Map<string, ServiceSummaryRow[]>();
    for (const row of givingByService.data?.data ?? []) {
      const existing = map.get(row.occurrenceId) ?? [];
      existing.push(row);
      map.set(row.occurrenceId, existing);
    }
    return map;
  }, [givingByService.data?.data]);

  useEffect(() => {
    if (!createOpen || !programId) return;
    const program = programRows.find((row) => row.id === programId);
    if (!program) return;
    if (createDatesLoading) return;

    const selectable = choosableOfferingDates(program, today, datesOnList);
    if (selectable.length === 0) {
      setVenueId(program.venueId);
      setServiceDate(null);
      return;
    }

    setServiceDate((current) => {
      const next =
        current && selectable.includes(current)
          ? current
          : defaultOfferingDate(selectable, today);
      if (!next) return current;
      const detail = (programDayDetails.data?.data ?? []).find((row) =>
        row.dates.includes(next),
      );
      const existing = occurrencesByDate.get(next);
      setVenueId(detail?.venueId ?? existing?.venueId ?? program.venueId);
      return next;
    });
  }, [
    createOpen,
    programId,
    programRows,
    programDayDetails.data?.data,
    datesOnList,
    occurrencesByDate,
    createDatesLoading,
    today,
  ]);

  useEffect(() => {
    if (!createOpen) return;
    if (!programId && programRows[0]) {
      setProgramId(programRows[0].id);
    }
  }, [createOpen, programId, programRows]);

  const createOccurrence = useMutation({
    mutationFn: async () => {
      if (!serviceDate) throw new Error('DATE_REQUIRED');
      if (!programId) throw new Error('PROGRAM_REQUIRED');
      const existing = occurrencesByDate.get(serviceDate);
      if (existing) return existing;
      return api.post<Occurrence>(`/programs/${programId}/occurrences`, {
        occurrenceDate: serviceDate,
        venueId,
      });
    },
    onSuccess: (row) => {
      toast.success(t(`${ns}.created`));
      setCreateOpen(false);
      setProgramId(null);
      void queryClient.invalidateQueries({ queryKey: ['occurrences'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'insights'] });
      void navigate(`/giving/services/${row.id}`);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const programOptions = useMemo(
    () =>
      programRows.map((program) => ({
        value: program.id,
        label: program.name,
      })),
    [programRows],
  );

  const currency = church?.baseCurrency ?? 'GHS';

  const columns: DataTableColumn<Occurrence>[] = [
    {
      id: 'date',
      header: t(`${ns}.columns.date`),
      cell: (row) => formatDate(row.occurrenceDate),
    },
    {
      id: 'name',
      header: t(`${ns}.columns.service`),
      cell: (row) => row.programName ?? '—',
    },
    {
      id: 'preacher',
      header: t(`${ns}.columns.preacher`),
      cell: (row) => row.preacherName ?? row.guestSpeaker ?? '—',
    },
    {
      id: 'venue',
      header: t(`${ns}.columns.venue`),
      cell: (row) => row.venueName ?? '—',
      hideBelowMd: true,
    },
    {
      id: 'given',
      header: t(`${ns}.columns.given`),
      align: 'right',
      cell: (row) => {
        const summaries = totalsByOccurrence.get(row.id) ?? [];
        const { amounts } = occurrenceMoneyTotals(summaries);
        return <MoneyTotalsStack items={amounts} size="sm" />;
      },
    },
    {
      id: 'collections',
      header: t(`${ns}.columns.collections`),
      cell: (row) =>
        String(occurrenceMoneyTotals(totalsByOccurrence.get(row.id) ?? []).count),
      hideBelowMd: true,
    },
  ];

  return (
    <div>
      <CollapsibleInsights
        summary={[
          {
            label: t(`${ns}.listInsights.total`),
            value: formatInsightsMoneySummary(insights.data, currency),
          },
          {
            label: t(`${ns}.listInsights.gifts`),
            value: insights.data ? formatNumber(insights.data.count) : '…',
          },
        ]}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {insightsHasMultipleCurrencies(insights.data) ? (
            insights.data!.byCurrency!.map((item) => (
              <StatCard
                key={item.currency}
                label={t(`${ns}.listInsights.total`)}
                money={{ amount: item.amountMinor, currency: item.currency }}
                icon={<HandCoins className="size-4" aria-hidden />}
                loading={insights.isLoading}
              />
            ))
          ) : (
            <StatCard
              label={t(`${ns}.listInsights.total`)}
              money={
                insights.data
                  ? { amount: insights.data.amountMinor, currency: insights.data.currency }
                  : undefined
              }
              icon={<HandCoins className="size-4" aria-hidden />}
              loading={insights.isLoading}
              comparison={
                insights.data
                  ? {
                      change: insights.data.comparison.change,
                      label: t(`${ns}.listInsights.vsPreviousPeriod`),
                      formatted: (() => {
                        const change = insights.data.comparison.change;
                        const formatted = formatPercent(Math.abs(change));
                        if (change > 0) return `+${formatted}`;
                        if (change < 0) return `-${formatted}`;
                        return formatted;
                      })(),
                    }
                  : undefined
              }
            />
          )}
          <StatCard
            label={t(`${ns}.listInsights.gifts`)}
            value={insights.data ? formatNumber(insights.data.count) : undefined}
            icon={<Gift className="size-4" aria-hidden />}
            loading={insights.isLoading}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <GivingTrendChart
            title={t(`${ns}.listInsights.trend`)}
            emptyTitle={t('contributions.insights.trendEmpty')}
            dateFrom={dateFrom}
            dateTo={dateTo}
            baseCurrency={church?.baseCurrency ?? 'GHS'}
            programKind={kind}
            fallbackPoints={(insights.data?.trend ?? []).map((point) => ({
              label: point.label,
              value: point.amountMinor,
              grain: point.grain,
            }))}
            fallbackLoading={insights.isLoading}
            height={200}
          />
          {(insights.data?.byFund.length ?? 0) > 0 ? (
            <ChartCard title={t(`${ns}.listInsights.byFund`)} height={200}>
              <SimpleBarChart
                orientation="horizontal"
                data={(insights.data?.byFund ?? []).map((row) => ({
                  label:
                    (insights.data?.byFund ?? []).some(
                      (other) =>
                        other.fundId === row.fundId && other.currency !== row.currency,
                    )
                      ? `${row.fundName} (${row.currency})`
                      : row.fundName,
                  value: row.amountMinor,
                  currency: row.currency,
                }))}
                formatValue={(value, item) =>
                  formatMoney(value, item?.currency ?? church?.baseCurrency ?? 'GHS', {
                    showSymbol: true,
                  })
                }
                height={180}
              />
            </ChartCard>
          ) : null}
        </div>
      </CollapsibleInsights>

      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap gap-3">
          <DateRangeFilter
            value={dateRange}
            onChange={onDateRangeChange}
            labels={{
              today: tCommon('date.today'),
              yesterday: tCommon('date.yesterday'),
              thisWeek: tCommon('date.thisWeek'),
              lastWeek: tCommon('date.lastWeek'),
              thisMonth: tCommon('date.thisMonth'),
              lastMonth: tCommon('date.lastMonth'),
              thisQuarter: tCommon('date.thisQuarter'),
              thisYear: tCommon('date.thisYear'),
              lastYear: tCommon('date.lastYear'),
              allTime: tCommon('date.allTime'),
              custom: tCommon('date.custom'),
              from: t('form.dateFrom'),
              to: t('form.dateTo'),
            }}
            presets={LIST_FILTER_DATE_RANGE_PRESETS}
          />
          <div className="w-56 min-w-[12rem]">
            <Listbox
              value={filterProgramId}
              onChange={setFilterProgramId}
              options={programOptions}
              placeholder={t(`${ns}.filterAll`)}
              searchable
              searchPlaceholder={tCommon('search.placeholder')}
              emptyMessage={tCommon('search.noResults')}
              clearable
              clearLabel={t(`${ns}.filterAll`)}
              disabled={!hasPrograms}
            />
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          {canRecord && hasPrograms ? (
            <Button
              variant="outline"
              onClick={() => {
                setRecordSession((current) => current + 1);
                setRecordOpen(true);
              }}
            >
              {t(`${ns}.recordOffering`)}
            </Button>
          ) : null}
          {programs.isLoading ? (
            <Button loading disabled>
              {t(`${ns}.create`)}
            </Button>
          ) : hasPrograms ? (
            <Button
              onClick={() => {
                const initialProgramId = filterProgramId ?? programRows[0]?.id ?? null;
                setProgramId(initialProgramId);
                if (!initialProgramId) {
                  setServiceDate(todayIso());
                }
                setCreateOpen(true);
              }}
            >
              {t(`${ns}.create`)}
            </Button>
          ) : canCreateGathering ? (
            <Button asChild>
              <Link to={ministryPath}>{t(`${ns}.goToPrograms`)}</Link>
            </Button>
          ) : null}
        </div>
      </div>

      {!hasPrograms && !programs.isLoading ? (
        <p className="mb-4 text-body-sm text-fg-secondary">
          {t(canCreateGathering ? `${ns}.noPrograms` : `${ns}.noProgramsUnavailable`)}
        </p>
      ) : null}

      <DataTable
        rows={listRows}
        columns={columns}
        rowKey={(row) => row.id}
        loading={occurrences.isLoading}
        empty={<p className="text-body-sm text-fg-secondary">{t(`${ns}.empty`)}</p>}
        onRowClick={(row) => void navigate(`/giving/services/${row.id}`)}
      />

      <Dialog
        open={createOpen}
        onOpenChange={(next) => {
          setCreateOpen(next);
          if (!next) setProgramId(null);
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')}>
          <form
            className="min-h-0"
            onSubmit={(event) => {
              event.preventDefault();
              createOccurrence.mutate();
            }}
          >
            <DialogBody
              className="max-h-[calc(92dvh-3rem)]"
              contentClassName="flex flex-col gap-4"
            >
              <DialogHeader>
                <DialogTitle>{t(`${ns}.create`)}</DialogTitle>
              </DialogHeader>
              <div className="flex flex-col gap-1.5">
                <Label>{t(`${ns}.program`)}</Label>
                <Listbox
                  value={programId ?? ''}
                  onChange={(value) => {
                    setProgramId(value);
                  }}
                  options={programOptions}
                  searchable
                  searchPlaceholder={tCommon('search.placeholder')}
                  emptyMessage={tCommon('search.noResults')}
                  popoverPortal="dialog"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t(`${ns}.chooseDay`)}</Label>
                {createDatesLoading ? (
                  <p className="text-body-sm text-fg-secondary">{tCommon('state.loading')}</p>
                ) : noScheduleDates ? (
                  <p className="text-body-sm text-fg-secondary">{t(`${ns}.noDaysAvailable`)}</p>
                ) : allDaysOnList ? (
                  <p className="text-body-sm text-fg-secondary">{t(`${ns}.allDaysOnList`)}</p>
                ) : (
                  <>
                    <Calendar
                      value={serviceDate}
                      onChange={(date) => {
                        setServiceDate(date);
                        const detail = (programDayDetails.data?.data ?? []).find((row) =>
                          row.dates.includes(date),
                        );
                        const existing = occurrencesByDate.get(date);
                        setVenueId(
                          detail?.venueId ??
                            existing?.venueId ??
                            selectedProgram?.venueId ??
                            null,
                        );
                      }}
                      min={offeringPastHorizon}
                      max={offeringFutureHorizon}
                      disabledDates={offeringDisabledDates}
                      markedDates={offeringMarkedDates}
                      popoverPortal="dialog"
                      previousMonthLabel={tPrograms('calendar.previousMonth')}
                      nextMonthLabel={tPrograms('calendar.nextMonth')}
                      className="mx-auto w-full max-w-[18rem]"
                    />
                    <p className="text-body-sm text-fg-secondary">{t(`${ns}.calendarHint`)}</p>
                  </>
                )}
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t(`${ns}.venue`)}</Label>
                <VenueSelect value={venueId} onChange={setVenueId} popoverPortal="dialog" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                  {tCommon('action.cancel')}
                </Button>
                <Button
                  type="submit"
                  loading={createOccurrence.isPending}
                  disabled={
                    !programId ||
                    !serviceDate ||
                    createDatesLoading ||
                    noScheduleDates ||
                    allDaysOnList
                  }
                >
                  {t(`${ns}.create`)}
                </Button>
              </DialogFooter>
            </DialogBody>
          </form>
        </DialogContent>
      </Dialog>

      <RecordOfferingDialog
        key={recordSession}
        open={recordOpen}
        onOpenChange={setRecordOpen}
        kind={kind}
        initialProgramId={filterProgramId}
      />
    </div>
  );
};

const ServiceOfferingDetail = ({ occurrenceId }: { occurrenceId: string }) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const church = useChurchStore((state) => state.church);
  const membership = useChurchStore((state) => state.membership);

  const [editOpen, setEditOpen] = useState(false);
  const [programName, setProgramName] = useState('');
  const [serviceDate, setServiceDate] = useState<string | null>(null);
  const [venueId, setVenueId] = useState<string | null>(null);
  const [recordOpen, setRecordOpen] = useState(false);
  const [recordSession, setRecordSession] = useState(0);
  const [batchOpen, setBatchOpen] = useState(false);
  const [expectedMinor, setExpectedMinor] = useState<number | null>(null);
  const [balanceTarget, setBalanceTarget] = useState<Batch | null>(null);
  const [postTarget, setPostTarget] = useState<Batch | null>(null);
  const [detailContributionId, setDetailContributionId] = useState<string | null>(null);
  const [voidTarget, setVoidTarget] = useState<Contribution | null>(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidPassword, setVoidPassword] = useState('');
  const [reasonTarget, setReasonTarget] = useState<Contribution | null>(null);

  const canRecord = hasPermission(membership, PERMISSIONS.GIVING_RECORD);
  const canManageBatches = hasPermission(membership, PERMISSIONS.GIVING_MANAGE_BATCHES);
  const canEditService = hasPermission(membership, PERMISSIONS.PROGRAMS_EDIT);
  const canVoidContribution = hasPermission(membership, PERMISSIONS.GIVING_VOID);

  const insight = useQuery({
    queryKey: ['giving', 'summary', 'by-occurrence', occurrenceId],
    queryFn: () =>
      api.get<OccurrenceInsight>(
        `/giving/summary/by-occurrence?occurrenceId=${encodeURIComponent(occurrenceId)}`,
      ),
  });

  const contributions = useQuery({
    queryKey: ['giving', 'contributions', 'occurrence', occurrenceId],
    queryFn: () =>
      api.get<{ data: Contribution[]; totals?: ListMoneyTotals }>(
        `/giving/contributions?occurrenceId=${encodeURIComponent(occurrenceId)}&pageSize=50&includeVoided=true`,
      ),
  });

  const funds = useQuery({
    queryKey: ['giving', 'funds', 'active'],
    queryFn: () => api.get<{ data: Fund[] }>('/giving/funds?pageSize=100&activeOnly=true'),
  });

  const batches = useQuery({
    queryKey: ['giving', 'batches', occurrenceId],
    queryFn: () =>
      api.get<{ data: Batch[] }>(
        `/giving/batches?occurrenceId=${encodeURIComponent(occurrenceId)}&pageSize=20`,
      ),
  });

  const occ = insight.data?.occurrence;
  const ns = occ?.programKind === 'program' ? 'programOfferings' : 'services';
  const listPath =
    occ?.programKind === 'program' ? '/giving/services?tab=programs' : '/giving/services';

  useEffect(() => {
    if (!editOpen || !occ) return;
    setProgramName(occ.programName ?? '');
    setServiceDate(occ.occurrenceDate);
    setVenueId(occ.venueId);
  }, [editOpen, occ]);

  const saveService = useMutation({
    mutationFn: async () => {
      if (!occ || !serviceDate) throw new Error('DATE_REQUIRED');
      const name = programName.trim();
      if (name && name !== (occ.programName ?? '')) {
        await api.patch(`/programs/${occ.programId}`, { name });
      }
      return api.patch<Occurrence>(`/occurrences/${occurrenceId}`, {
        occurrenceDate: serviceDate,
        venueId,
      });
    },
    onSuccess: () => {
      toast.success(t(`${ns}.saved`));
      setEditOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['giving', 'summary', 'by-occurrence', occurrenceId] });
      void queryClient.invalidateQueries({ queryKey: ['occurrences'] });
      void queryClient.invalidateQueries({ queryKey: ['programs'] });
      void queryClient.invalidateQueries({ queryKey: ['giving', 'summary', 'by-service'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const openBatch = useMutation({
    mutationFn: () =>
      api.post<Batch>('/giving/batches', {
        occurrenceId,
        expectedTotalMinor: expectedMinor ?? 0,
        currency: church?.baseCurrency ?? 'GHS',
        description: occ ? `${occ.programName ?? ''} · ${occ.occurrenceDate}` : null,
      }),
    onSuccess: () => {
      toast.success(t(`${ns}.batchOpened`));
      setBatchOpen(false);
      setExpectedMinor(null);
      void queryClient.invalidateQueries({ queryKey: ['giving', 'batches', occurrenceId] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const balanceBatch = useMutation({
    mutationFn: (batchId: string) => api.post<Batch>(`/giving/batches/${batchId}/balance`, {}),
    onSuccess: () => {
      toast.success(t(`${ns}.batchBalanced`));
      setBalanceTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['giving', 'batches', occurrenceId] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const postBatch = useMutation({
    mutationFn: (batchId: string) => api.post<Batch>(`/giving/batches/${batchId}/post`, {}),
    onSuccess: () => {
      toast.success(t(`${ns}.batchPosted`));
      setPostTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['giving', 'batches', occurrenceId] });
      void queryClient.invalidateQueries({
        queryKey: ['giving', 'contributions', 'occurrence', occurrenceId],
      });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const invalidateCollections = () => {
    void queryClient.invalidateQueries({
      queryKey: ['giving', 'contributions', 'occurrence', occurrenceId],
    });
    void queryClient.invalidateQueries({
      queryKey: ['giving', 'summary', 'by-occurrence', occurrenceId],
    });
    void queryClient.invalidateQueries({ queryKey: ['giving', 'batches', occurrenceId] });
    void queryClient.invalidateQueries({ queryKey: ['giving', 'summary', 'by-service'] });
  };

  const voidContribution = useMutation({
    mutationFn: () =>
      api.post<Contribution>(`/giving/contributions/${voidTarget!.id}/void`, {
        reason: voidReason,
        password: voidPassword,
      }),
    onSuccess: () => {
      toast.success(t('voided'));
      setVoidTarget(null);
      setVoidReason('');
      setVoidPassword('');
      invalidateCollections();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const contributionColumns: DataTableColumn<Contribution>[] = [
    {
      id: 'date',
      header: t('contributions.columns.date'),
      cell: (row) => formatDate(row.contributionDate),
    },
    {
      id: 'amount',
      header: t('contributions.columns.amount'),
      money: (row) => ({ amount: row.amountMinor, currency: row.currency }),
    },
    {
      id: 'fund',
      header: t('contributions.columns.fund'),
      cell: (row) => funds.data?.data.find((fund) => fund.id === row.fundId)?.name ?? '—',
    },
    {
      id: 'giver',
      header: t('contributions.columns.giver'),
      cell: (row) => {
        if (row.identityHidden) return t('contributions.identityHidden');
        if (!row.memberId) return '—';
        return <MemberNameLink memberId={row.memberId} name={row.giverName ?? ''} />;
      },
    },
    {
      id: 'method',
      header: t('contributions.columns.method'),
      cell: (row) => t(`methods.${row.method}` as 'methods.cash'),
    },
    {
      id: 'receipt',
      header: t('contributions.columns.receipt'),
      cell: (row) => t('contributions.receipt', { number: row.receiptNumber }),
      hideBelowMd: true,
    },
    {
      id: 'status',
      header: t('contributions.columns.status'),
      cell: (row) =>
        row.isVoided ? (
          <Badge variant="danger">{t('contributions.voided')}</Badge>
        ) : (
          '—'
        ),
    },
  ];

  const totalsCurrency = church?.baseCurrency ?? 'GHS';
  const occurrenceAmounts = occurrenceTotalsAmounts(insight.data?.totals);

  return (
    <div>
      <PageHeader
        title={occ?.programName ?? t(`${ns}.detailTitle`)}
        description={
          occ
            ? t(`${ns}.detailDescription`, { date: formatDate(occ.occurrenceDate) })
            : t('offerings.description')
        }
        breadcrumbs={[
          { label: t(`${ns}.title`), to: listPath },
          {
            label: occ
              ? `${occ.programName ?? t(`${ns}.detailTitle`)} · ${formatDate(occ.occurrenceDate)}`
              : t(`${ns}.detailTitle`),
          },
        ]}
        headerActions={
          <div className="flex flex-wrap justify-end gap-2">
            {canEditService && occ?.status === 'scheduled' ? (
              <Button size="sm" variant="outline" onClick={() => setEditOpen(true)}>
                {t(`${ns}.edit`)}
              </Button>
            ) : null}
            {canManageBatches ? (
              <Button size="sm" variant="outline" onClick={() => setBatchOpen(true)}>
                {t(`${ns}.openBatch`)}
              </Button>
            ) : null}
            {canRecord ? (
              <Button
                size="sm"
                onClick={() => {
                  setRecordSession((current) => current + 1);
                  setRecordOpen(true);
                }}
              >
                {t(`${ns}.recordOffering`)}
              </Button>
            ) : null}
          </div>
        }
      />

      <CollapsibleInsights
        summary={[
          {
            label: t(`${ns}.insights.total`),
            value: formatOccurrenceTotalsSummary(insight.data?.totals),
          },
          {
            label: t(`${ns}.insights.collections`),
            value: insight.data ? String(insight.data.totals.count) : '…',
          },
          {
            label: t(`${ns}.insights.funds`),
            value: insight.data ? String(insight.data.funds.length) : '…',
          },
        ]}
      >
        <div className="grid gap-4 sm:grid-cols-3">
          {occurrenceAmounts.length > 1 ? (
            occurrenceAmounts.map((item) => (
              <StatCard
                key={item.currency}
                label={t(`${ns}.insights.total`)}
                money={{ amount: item.amountMinor, currency: item.currency }}
                loading={insight.isLoading}
              />
            ))
          ) : (
            <StatCard
              label={t(`${ns}.insights.total`)}
              money={
                occurrenceAmounts[0]
                  ? {
                      amount: occurrenceAmounts[0].amountMinor,
                      currency: occurrenceAmounts[0].currency,
                    }
                  : undefined
              }
              loading={insight.isLoading}
            />
          )}
          <StatCard
            label={t(`${ns}.insights.collections`)}
            value={String(insight.data?.totals.count ?? 0)}
            loading={insight.isLoading}
          />
          <StatCard
            label={t(`${ns}.insights.funds`)}
            value={String(insight.data?.funds.length ?? 0)}
            loading={insight.isLoading}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{t(`${ns}.insights.byFund`)}</CardTitle>
            <CardDescription>{t(`${ns}.insights.byFundDescription`)}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {(insight.data?.funds.length ?? 0) === 0 ? (
              <p className="text-body-sm text-fg-secondary">{t(`${ns}.insights.empty`)}</p>
            ) : (
              insight.data?.funds.map((row) => (
                <div
                  key={`${row.fundId}-${row.currency}`}
                  className="flex items-center justify-between gap-3 border-b border-border pb-2 last:border-0"
                >
                  <div>
                    <p className="text-body-sm font-medium">{row.fundName}</p>
                    <p className="text-caption text-fg-secondary">
                      {fundTypeLabel(row, t)} ·{' '}
                      {t(`${ns}.insights.collectionCount`, { count: row.count })}
                    </p>
                  </div>
                  <MoneyDisplay amount={row.amountMinor} currency={row.currency} />
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </CollapsibleInsights>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>{t(`${ns}.batches`)}</CardTitle>
          <CardDescription>{t(`${ns}.batchesDescription`)}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(batches.data?.data.length ?? 0) === 0 ? (
            <p className="text-body-sm text-fg-secondary">{t(`${ns}.batchesEmpty`)}</p>
          ) : (
            batches.data?.data.map((batch) => (
              <div
                key={batch.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2"
              >
                <div>
                  <Badge variant="neutral">{t(`${ns}.batchStatus.${batch.status}`)}</Badge>
                  <p className="mt-1 text-caption text-fg-secondary">
                    {t(`${ns}.batchLineCount`, { count: batch.lineCount })}
                  </p>
                  <div className="mt-1 flex gap-3 text-body-sm">
                    <MoneyDisplay amount={batch.actualTotalMinor} currency={batch.currency} />
                    <span className="text-fg-secondary">/</span>
                    <MoneyDisplay amount={batch.expectedTotalMinor} currency={batch.currency} />
                  </div>
                </div>
                {canManageBatches ? (
                  <div className="flex justify-end gap-2">
                    {batch.status === 'open' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setBalanceTarget(batch)}
                      >
                        {t(`${ns}.balanceBatch`)}
                      </Button>
                    ) : null}
                    {batch.status === 'balanced' ? (
                      <Button size="sm" onClick={() => setPostTarget(batch)}>
                        {t(`${ns}.postBatch`)}
                      </Button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t(`${ns}.contributions`)}</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            rows={contributions.data?.data ?? []}
            columns={contributionColumns}
            rowKey={(row) => row.id}
            loading={contributions.isLoading}
            onRowClick={(row) => setDetailContributionId(row.id)}
            empty={
              <p className="text-body-sm text-fg-secondary">{t(`${ns}.contributionsEmpty`)}</p>
            }
            totals={dataTableMoneyColumnTotals('amount', contributions.data?.totals, totalsCurrency)}
            totalsLabel={t('contributions.totals')}
            rowActions={(row) => {
              if (row.isVoided) {
                return (
                  <Button size="sm" variant="outline" onClick={() => setReasonTarget(row)}>
                    {t('contributions.viewReason')}
                  </Button>
                );
              }
              if (!canVoidContribution || !row.canVoid) return null;
              return (
                <div className="flex justify-end gap-2">
                  <Button size="sm" variant="outline" onClick={() => setVoidTarget(row)}>
                    {t('contributions.void')}
                  </Button>
                </div>
              );
            }}
            actionsHeader={canVoidContribution ? tCommon('table.actions') : undefined}
          />
        </CardContent>
      </Card>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
          <DialogHeader>
            <DialogTitle>{t(`${ns}.edit`)}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!programName.trim() || !serviceDate) return;
              saveService.mutate();
            }}
          >
            <div className="flex flex-col gap-1.5">
              <Label>{t(`${ns}.programName`)}</Label>
              <Input
                value={programName}
                onChange={(event) => setProgramName(event.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t(`${ns}.date`)}</Label>
              <DatePicker value={serviceDate} onChange={setServiceDate} popoverPortal="dialog" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t(`${ns}.venue`)}</Label>
              <VenueSelect value={venueId} onChange={setVenueId} popoverPortal="dialog" />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                loading={saveService.isPending}
                disabled={!programName.trim() || !serviceDate}
              >
                {tCommon('action.save')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <RecordOfferingDialog
        key={recordSession}
        open={recordOpen}
        onOpenChange={setRecordOpen}
        kind={occ?.programKind === 'program' ? 'program' : 'service'}
        initialProgramId={occ?.programId ?? null}
        initialServiceDate={occ?.occurrenceDate ?? null}
      />

      <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
        <DialogContent closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t(`${ns}.openBatch`)}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (expectedMinor == null) return;
              openBatch.mutate();
            }}
          >
            <Tip
              tipId={
                ns === 'programOfferings'
                  ? 'giving.programOfferings.batch'
                  : 'giving.serviceOfferings.batch'
              }
            />
            <div className="flex flex-col gap-1.5">
              <Label>{t(`${ns}.expectedTotal`)}</Label>
              <MoneyInput
                value={expectedMinor}
                onChange={setExpectedMinor}
                currency={church?.baseCurrency ?? 'GHS'}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setBatchOpen(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button type="submit" loading={openBatch.isPending}>
                {t(`${ns}.openBatch`)}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ContributionDetailDialog
        contributionId={detailContributionId}
        open={Boolean(detailContributionId)}
        onOpenChange={(open) => {
          if (!open) setDetailContributionId(null);
        }}
      />

      <Dialog
        open={Boolean(voidTarget)}
        onOpenChange={(next) => {
          if (!next) {
            setVoidTarget(null);
            setVoidReason('');
            setVoidPassword('');
          }
        }}
      >
        <DialogContent closeLabel={tCommon('action.close')}>
          <DialogHeader>
            <DialogTitle>{t('contributions.voidTitle')}</DialogTitle>
          </DialogHeader>
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (!voidReason.trim() || !voidPassword) return;
              voidContribution.mutate();
            }}
          >
            <p className="text-body-sm text-fg-secondary">{t('contributions.voidDescription')}</p>
            <div className="flex flex-col gap-1.5">
              <Label>{t('contributions.voidReason')}</Label>
              <Textarea
                value={voidReason}
                onChange={(event) => setVoidReason(event.target.value)}
                rows={3}
                required
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="offering-void-password">{t('contributions.voidPassword')}</Label>
              <Input
                id="offering-void-password"
                type="password"
                value={voidPassword}
                onChange={(event) => setVoidPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
              <p className="text-caption text-fg-secondary">{t('contributions.voidPasswordHint')}</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setVoidTarget(null);
                  setVoidReason('');
                  setVoidPassword('');
                }}
              >
                {tCommon('action.cancel')}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                loading={voidContribution.isPending}
                disabled={!voidReason.trim() || !voidPassword}
              >
                {t('contributions.voidConfirm')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <VoidReasonDialog
        open={Boolean(reasonTarget)}
        onClose={() => setReasonTarget(null)}
        reason={reasonTarget?.voidReason}
        voidedByName={reasonTarget?.voidedByName}
      />

      <ConfirmDialog
        open={balanceTarget !== null}
        onOpenChange={(next) => {
          if (!next) setBalanceTarget(null);
        }}
        title={t('confirm.balanceBatch.title')}
        description={t('confirm.balanceBatch.description')}
        confirmLabel={t('confirm.balanceBatch.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={balanceBatch.isPending}
        onConfirm={() => {
          if (balanceTarget) balanceBatch.mutate(balanceTarget.id);
        }}
      />
      <ConfirmDialog
        open={postTarget !== null}
        onOpenChange={(next) => {
          if (!next) setPostTarget(null);
        }}
        title={t('confirm.postBatch.title')}
        description={t('confirm.postBatch.description')}
        confirmLabel={t('confirm.postBatch.confirm')}
        cancelLabel={tCommon('action.cancel')}
        loading={postBatch.isPending}
        onConfirm={() => {
          if (postTarget) postBatch.mutate(postTarget.id);
        }}
      />
    </div>
  );
};
